import React, { useMemo, useRef } from 'react';
import { useOpenLayers } from '../openlayers-context';
import { Container } from './ControlBar.styles';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import mergeRefs from 'react-merge-refs';
/* Consuming applications can import this component and pass Control components as children
 * to render them styled and positioned the way we expect.
 *
 *  - Renders div elements that Ol.Control objects target to render as a child of
 *  - Styled component enforces styling and and HTML/JSX enforces the order and positioning of Control components.
 *  - <Map /> and <Control /> can be siblings. This allows the consuming application to choose
 *    where the control bar is rendered and thus sized/positioned relative to.
 *
 *    This is important because the <Map /> can be explicitly positioned
 *    with a constant size (position: absolute; top:0; left:0; right:0;)/
 *    wheras the <ControlBar /> resizes and positions itself
 *    relative to the available space in the UI (the window the map can be seen through ).
 *
 * In LRP, this is imported and rendered as a sibling to <Map /> because the
 * map does not resize relative to the space available when the <InfoPanel /> is open.
 * Whereas the controls would need to be pushed over by the <InfoPanel />
 */

var ControlBar = _ref => {
  var {
    children,
    displayScaleBar
  } = _ref;
  var {
    layerSwitcherTargetRef,
    resetPositionTargetRef,
    addLayerTargetRef,
    drawTargetRef,
    modifyTargetRef,
    resetTargetRef,
    undoTargetRef,
    clusterRef,
    undoRedoFeaturesRef,
    measurementsTargetRef,
    validateFeaturesRef,
    loadGeoJSONRef,
    getShapesTargetRef,
    storybookControlRef,
    overlayElementRef
  } = useOpenLayers();
  var viewportToolbarBoundaryRef = useRef(null);
  var [measureRef, viewportBounds] = useMeasure({
    polyfill: ResizeObserver
  });
  var enhancedChildren = useMemo(() => React.Children.map(children, child => child && /*#__PURE__*/React.cloneElement(child, {
    viewportBoundaryRef: viewportToolbarBoundaryRef
  })), [children, viewportToolbarBoundaryRef]);
  return /*#__PURE__*/React.createElement(Container, {
    id: "ol-control-bar",
    ref: mergeRefs([viewportToolbarBoundaryRef, measureRef]),
    $condensed: !!(viewportBounds !== null && viewportBounds !== void 0 && viewportBounds.width) && viewportBounds.width < 900,
    displayscalebar: !!displayScaleBar
  }, /*#__PURE__*/React.createElement("div", {
    id: "ol-overlay-element",
    ref: overlayElementRef
  }), /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-toolbar-target"
  }, /*#__PURE__*/React.createElement("div", {
    ref: layerSwitcherTargetRef,
    id: "ol-controls-layer-switcher-target"
  }, enhancedChildren), /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-draw-palette-target"
  }, /*#__PURE__*/React.createElement("div", {
    ref: addLayerTargetRef,
    id: "ol-controls-toolbar-button-add-layer-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: drawTargetRef,
    id: "ol-controls-toolbar-button-draw-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: modifyTargetRef,
    id: "ol-controls-toolbar-button-modify-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: resetTargetRef,
    id: "ol-controls-toolbar-button-reset-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: undoTargetRef,
    id: "ol-controls-toolbar-button-undo-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: getShapesTargetRef,
    id: "ol-controls-toolbar-button-get-shapes-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: measurementsTargetRef,
    id: "ol-controls-toolbar-button-measurements-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: validateFeaturesRef,
    id: "ol-controls-toolbar-button-validate-features-target"
  }), /*#__PURE__*/React.createElement("div", {
    ref: loadGeoJSONRef,
    id: "ol-controls-toolbar-button-load-geojson"
  }), /*#__PURE__*/React.createElement("div", {
    ref: clusterRef,
    id: "ol-control-cluster-config"
  }), /*#__PURE__*/React.createElement("div", {
    ref: undoRedoFeaturesRef,
    id: "ol-control-undo-redo-features"
  }), /*#__PURE__*/React.createElement("div", {
    ref: storybookControlRef,
    id: "ol-control-storybook"
  }))), /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-target"
  }, /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-attribution-target"
  }), /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-scale-line-target"
  }), /*#__PURE__*/React.createElement("div", {
    id: "ol-controls-zoom-target"
  }, /*#__PURE__*/React.createElement("div", {
    ref: resetPositionTargetRef,
    id: "ol-controls-reset-position-target"
  }))));
};

export default ControlBar;